// ─── Generic CSV/XLSX export ────────────────────────────────────────────────
//
// Ported near-verbatim from TransactionTable/utils/exportTransactions.ts —
// the download mechanics (`downloadCsv`/`downloadXlsx`) were already fully
// generic; the "row -> cell text" logic is now driven by `ColumnMeta`
// (dataKey/getValue + isDate/isCurrency formatting) with a
// `specialCase.getCellText` escape hatch per column, instead of a hardcoded
// per-table switch statement.
//
// IMPORTANT (security): callers must only pass the *currently visible*
// column keys (already filtered by permission — e.g. a gated Market Value
// column should simply not exist in `columnMeta`/`visibleColumnKeys` for an
// unauthorized user). This module never reads from the full row object
// independent of the supplied column list, so there is no way for gated data
// to leak into an export as long as the caller's visible-column list is
// correct.

import { ColumnMeta, ColumnMetaMap, FilterValue, GroupConfig, isEnumFilterValue, isRangeFilterValue } from './types';
import { getNestedValue, groupGridRows, sortGroupedBuckets } from './gridEngine';

/**
 * Produces human-readable one-line summaries of the currently active
 * column/group filters + search — the same information shown by the preset
 * bar's "Filtered by:" badges, formatted for a plain-text export note (e.g.
 * `Division: Row Crop, Permanent Plantings`, `Market Value: ≥ 1,000,000`,
 * `Search: "walnut"`). Returns `[]` when nothing is filtering the row set.
 *
  * @param columnMeta - Mapping of column keys to their metadata (label, format, etc.).
  * @param filters - Active column filters keyed by column key, as `FilterValue` (enum array or range object).
  * @param groupFilters - Active group filters keyed by group ID, mapping to arrays of selected option values.
  * @param groupConfigs - Group configurations defining each group's label, ID, and available options.
  * @param searchQuery - An optional search query string entered by the user.
  * @returns An array of human-readable summary strings, one per active filter, or an empty array when nothing is filtering.
  */
export function buildFilterSummaryLines<TRow, K extends string>(
  columnMeta: ColumnMetaMap<TRow, K>,
  filters: Record<string, FilterValue>,
  groupFilters: Record<string, string[]>,
  groupConfigs: GroupConfig<K>[],
  searchQuery?: string,
): string[] {
  const lines: string[] = [];

  for (const [key, value] of Object.entries(filters)) {
    const meta = columnMeta[key as K];
    if (!meta) continue;
    if (isEnumFilterValue(value)) {
      if (value.length === 0) continue;
      lines.push(`${meta.label}: ${value.join(', ')}`);
    } else if (isRangeFilterValue(value)) {
      if (value.min == null && value.max == null) continue;
      const fmt = meta.formatAggregate ?? ((n: number) => n.toLocaleString());
      const text =
        value.min != null && value.max != null
          ? `${fmt(value.min)} – ${fmt(value.max)}`
          : value.min != null
            ? `≥ ${fmt(value.min)}`
            : `≤ ${fmt(value.max as number)}`;
      lines.push(`${meta.label}: ${text}`);
    }
  }

  for (const group of groupConfigs) {
    const selected = groupFilters[group.id] ?? [];
    if (selected.length === 0) continue;
    const labels = selected.map((v) => group.options.find((o) => o.value === v)?.label ?? v);
    lines.push(`${group.label}: ${labels.join(', ')}`);
  }

  if (searchQuery) lines.push(`Search: "${searchQuery}"`);

  return lines;
}

function resolveCellValue<TRow, K extends string>(row: TRow, meta: ColumnMeta<TRow, K> | undefined, columnKey: string): unknown {
  if (!meta) return undefined;
  if (meta.getValue) return meta.getValue(row);
  if (meta.dataKey) return getNestedValue(row, meta.dataKey);
  return getNestedValue(row, columnKey);
}

/** Returns a plain string value for a column + row — mirrors the cell renderer, generically. */
export function getCellText<TRow, K extends string>(columnKey: K, row: TRow, columnMeta: ColumnMetaMap<TRow, K>): string {
  const meta = columnMeta[columnKey];
  if (meta?.specialCase?.getCellText) return meta.specialCase.getCellText(row);

  const value = resolveCellValue(row, meta, columnKey);
  if (value == null || value === '') return '';
  if (meta?.isDate) {
    const d = new Date(String(value));
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US');
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

/**
 * Builds a 2-D array of [headers, ...rows] for the given (already
 * permission-filtered) visible columns + data.
 *
 * When `options.groupByColumnKey` is set (the table is currently row-grouped
 * on screen), the export mirrors that grouping instead of a flat sort order:
 * the group-by column is placed first (via `resolveExportColumnKeys`), and
 * rows are emitted group-by-group in the same bucket order shown on screen
 * (via `groupGridRows`/`sortGroupedBuckets`). Each group is introduced by a
 * labelled separator row (`<GroupColumnLabel>: <groupValue> (<n>)`) so the
 * groups read as distinct sub-tables in the CSV/spreadsheet rather than one
 * undifferentiated block.
 *
 * `options.notes` (e.g. from `buildFilterSummaryLines`) are emitted as
 * leading rows above the column header — one note per row in the first cell —
 * followed by a blank spacer, so an exported file records which filters were
 * active when it was generated.
 */
export function buildExportRows<TRow, K extends string>(
  visibleColumnKeys: K[],
  rows: TRow[],
  columnMeta: ColumnMetaMap<TRow, K>,
  options?: {
    groupByColumnKey?: K | null;
    sortColumns?: string[];
    sortOrders?: ('asc' | 'desc' | 'none')[];
    notes?: string[];
  },
): string[][] {
  const groupByColumnKey = options?.groupByColumnKey;
  const orderedColumnKeys = resolveExportColumnKeys(visibleColumnKeys, groupByColumnKey);
  const headers = orderedColumnKeys.map((k) => columnMeta[k].label);
  const width = orderedColumnKeys.length;
  const toRow = (row: TRow) => orderedColumnKeys.map((k) => getCellText(k, row, columnMeta));

  // Leading note rows (filter summary etc.) above the header, each padded to
  // the full column width, followed by a blank spacer.
  const noteRows: string[][] = [];
  if (options?.notes?.length) {
    for (const note of options.notes) {
      const r = new Array(width).fill('');
      r[0] = note;
      noteRows.push(r);
    }
    noteRows.push(new Array(width).fill(''));
  }

  if (!groupByColumnKey) {
    return [...noteRows, headers, ...rows.map(toRow)];
  }

  const buckets = sortGroupedBuckets(
    groupGridRows(rows, groupByColumnKey, columnMeta),
    groupByColumnKey,
    options?.sortColumns ?? [],
    options?.sortOrders ?? [],
    columnMeta,
  );

  const groupLabel = columnMeta[groupByColumnKey].label;
  const out: string[][] = [...noteRows, headers];

  for (const bucket of buckets) {
    // Blank spacer + a labelled separator row so each group reads as its own
    // sub-table. The label occupies the first cell; remaining cells are
    // padded blank to keep every row the same width.
    const separator = new Array(width).fill('');
    separator[0] = `${groupLabel}: ${bucket.label} (${bucket.rows.length})`;
    out.push(new Array(width).fill(''));
    out.push(separator);
    for (const row of bucket.rows) out.push(toRow(row));
  }

  return out;
}

/**
 * Orders the exported column list for a (possibly grouped) export. When
 * `groupByColumnKey` is set (the table is row-grouped), that column is moved
 * to the FIRST position — added if it was hidden from the on-screen table, or
 * relocated to the front if it was already visible — so every exported row's
 * leading cell records which group it belongs to. No-op (returns
 * `visibleColumnKeys` unchanged) when not grouped.
 */
export function resolveExportColumnKeys<K extends string>(visibleColumnKeys: K[], groupByColumnKey?: K | null): K[] {
  if (!groupByColumnKey) return visibleColumnKeys;
  return [groupByColumnKey, ...visibleColumnKeys.filter((k) => k !== groupByColumnKey)];
}

/** Triggers a browser CSV download. */
export function downloadCsv(rows: string[][], filename = 'export.csv'): void {
  const csv = rows.map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(',')).join('\r\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Triggers a browser XLSX download — dynamically imports xlsx to keep bundle lean. */
export async function downloadXlsx(rows: string[][], filename = 'export.xlsx', sheetName = 'Sheet1'): Promise<void> {
  const { utils, writeFile } = await import('xlsx');
  const ws = utils.aoa_to_sheet(rows);
  const wb = utils.book_new();
  utils.book_append_sheet(wb, ws, sheetName);
  writeFile(wb, filename);
}
